/**
 * @todo add/update JSDoc tags to document your code
 * (see http://usejsdoc.org/ for information on JSDoc tags),
 * then remove this comment block.
 */

/** IconButton functional (stateless) component */
import React from 'react';
import PropTypes from 'prop-types';
import { keys } from 'lodash';

// Styles
import './button.scss';

// Components
import ImageAsset from '../image-asset/image-asset';

/**
 * Using those types:
 *      - iconSVG: inlines an SVG file's code in the HTML. Has a huge impact on quantity of code if repeated.
 *      - iconBackground: uses a class to show a specific icon. Classes have to be updated and hover and active images
 *      have to be created if needed, but has a low impact when used repeatedly.
 *      - iconText: similar to iconSVG but allows for a visible text after the image.
 *      - text: just display a regular button with text, no image.
 * @type {{iconText: string, iconSVG: string, text: string, iconBackground: string}}
 */
export const BUTTON_STYLES = {
    iconSVG: 'iconSVG',
    iconBackground: 'iconBackground',
    iconText: 'iconText',
    text: 'text'
};
const STYLES_NAMES = keys(BUTTON_STYLES);
/**
 * IconButton pro-forma template for stateless component
 * @todo - add a proper description of the component
 *
 * @access public
 * @param {string} text string describing the component
 * @returns JSX render of the component
 */
const Button = ({
    forwardRef,
    buttonType,
    buttonStyle,
    ariaProps,
    visibleText,
    importedClasses,
    imageClassName,
    iconName,
    isBtnDisabled,
    noTitle,
    callback,
    assetTitle
}) => {
    let btnClasses = `button ${importedClasses}`;

    const callbackAction = (e) => {
        if (callback && !isBtnDisabled) {
            callback(e);
        }
    };

    /*
    By default, the content of Aria-Label is propagated to the title of icn buttons, unless stated otherwise through
    the `noTitle` flag.
     */
    const titleProps = !noTitle && ariaProps && ariaProps['aria-label'] ? { title: ariaProps['aria-label'] } : {};
    let buttonTemplate = '';
    switch (buttonStyle) {
        case BUTTON_STYLES.iconSVG:
            btnClasses = `${btnClasses} button--icon-svg`;
            buttonTemplate = (
                <button
                    ref={forwardRef}
                    type={buttonType}
                    {...titleProps}
                    {...ariaProps}
                    disabled={isBtnDisabled}
                    onClick={callbackAction}
                    className={btnClasses}>
                    <ImageAsset parentClass={imageClassName} type={iconName} parentTitle={assetTitle} />
                    <span className="sr-only">
                        {visibleText || (ariaProps && ariaProps['aria-label'])}
                    </span>
                </button>
            );
            break;

        case BUTTON_STYLES.iconBackground:
            btnClasses = `${btnClasses} button--icon-background`;
            buttonTemplate = (
                <button
                    ref={forwardRef}
                    type={buttonType}
                    {...titleProps}
                    {...ariaProps}
                    disabled={isBtnDisabled}
                    onClick={callbackAction}
                    className={btnClasses}>
                    <span className="sr-only">
                        {visibleText || (ariaProps && ariaProps['aria-label'])}
                    </span>
                </button>
            );
            break;

        case BUTTON_STYLES.iconText:
            btnClasses = `${btnClasses} button--icon-text`;
            buttonTemplate = (
                <button
                    ref={forwardRef}
                    type={buttonType}
                    {...ariaProps}
                    disabled={isBtnDisabled}
                    onClick={callbackAction}
                    className={btnClasses}>
                    <ImageAsset parentClass={imageClassName} type={iconName} parentTitle={assetTitle} />
                    {visibleText}
                </button>
            );
            break;

        case BUTTON_STYLES.text:
        default:
            buttonTemplate = (
                <button
                    ref={forwardRef}
                    type={buttonType}
                    {...ariaProps}
                    disabled={isBtnDisabled}
                    onClick={callbackAction}
                    className={btnClasses}>
                    {visibleText}
                </button>
            );
    }

    return (
        buttonTemplate
    );
};

/**
 * Define the default values for the properties being used
 */
Button.defaultProps = {
    buttonType: 'button',
    buttonStyle: BUTTON_STYLES.text,
    importedClasses: '',
    imageClassName: null,
    isBtnDisabled: false
};

/**
 * Define the type of properties that can be set on this component
 */
Button.propTypes = {
    forwardRef: PropTypes.any,
    importedClasses: PropTypes.string,
    buttonType: PropTypes.string,
    buttonStyle: PropTypes.oneOf(STYLES_NAMES),
    imageClassName: PropTypes.string,
    iconName: PropTypes.string,
    ariaProps: PropTypes.object,
    visibleText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isBtnDisabled: PropTypes.bool,
    noTitle: PropTypes.bool,
    callback: PropTypes.func,
    assetTitle: PropTypes.string
};

export default React.forwardRef((props, ref) => (<Button forwardRef={ref} {...props} />));



// WEBPACK FOOTER //
// ./components/button/button.js